import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Heart, Target, Users, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { WaitlistForm } from "../components/WaitlistForm";
import { CounterAnimation } from "../components/CounterAnimation";
import { BecomeLogo } from "../components/BecomeLogo";
import { VideoBackground } from "../components/VideoBackground";

// Video URLs from Mixkit (free stock videos)
// Theme: Forward momentum (left-to-right) from "stuck" to empowered
// Target: Mid 20s to late 30s, real struggle, wanting more, finding direction
const HERO_VIDEO = "https://assets.mixkit.co/videos/134/134-720.mp4"; // Man walking through city, urban, determined, forward movement
const HERO_VIDEO_FALLBACK = "https://assets.mixkit.co/videos/35830/35830-720.mp4"; // Person walking between fields, journey forward

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true }
};

const differentiators = [
  {
    icon: Target,
    title: "We succeed when you achieve your goals",
    description: "We don't want you subscribed forever. We want you to thrive beyond us."
  },
  {
    icon: Users,
    title: "Your coordinated support team",
    description: "Emotional Readiness Guide, Career Discovery Coach, Resource Navigator, and Transition Specialist, all working together."
  },
  {
    icon: Heart,
    title: "Clinical foundation",
    description: "Designed with licensed clinical oversight to support your emotional wellbeing."
  },
  {
    icon: Shield,
    title: "Privacy-first architecture",
    description: "Built by a cybersecurity engineer using Zero Trust architecture. Your data is yours."
  }
];

const phases = [
  { name: "Reflect", description: "Process what you're going through" },
  { name: "Discover", description: "Explore who you could become" },
  { name: "Become", description: "Take action with coordinated support" }
];

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Video Background */}
        <VideoBackground
          src={HERO_VIDEO}
          fallbackSrc={HERO_VIDEO_FALLBACK}
          overlay="bg-deep-violet/60"
        />

        {/* Parallax Floating Elements (on top of video) */}
        <motion.div
          style={{ y }}
          className="absolute inset-0 overflow-hidden pointer-events-none z-[1]"
        >
          {/* Floating orbs */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-brand-peach/10 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-brand-lavender/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1s" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-primary/5 rounded-full blur-3xl" />
        </motion.div>

        <motion.div
          className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center"
        >
          {/* Floating Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8 float-element inline-block"
            data-testid="hero-logo"
          >
            <BecomeLogo size="hero" showText={false} />
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-base sm:text-lg mb-6"
            data-testid="hero-tagline"
          >
            Reflect. Discover. Become.
          </motion.p>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            style={{ fontStyle: 'normal' }}
            data-testid="hero-headline"
          >
            You already know something needs to change.
            <br />
            <span className="gradient-text font-normal" style={{ fontStyle: 'normal' }}>We help you make it real.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Transformation support that meets you where you are: emotionally, financially, and professionally. We walk with you until you don't need us anymore.
          </motion.p>

          {/* Waitlist Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <WaitlistForm variant="dark" />
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-text-light/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-1.5 bg-brand-peach rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* How It Works Preview */}
      <section className="py-24 bg-warm-bone" data-testid="how-it-works-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-body text-3xl sm:text-4xl text-text-dark mb-4 font-semibold tracking-tight">
              Your Journey in Three Steps
            </h2>
            <p className="text-text-dark/60 text-lg max-w-2xl mx-auto">
              You move forward when YOU'RE ready, not on someone else's timeline.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.name}
                {...stagger}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className="relative group h-full"
              >
                <div className="h-full p-8 rounded-2xl bg-white shadow-soft border border-gray-100 hover:border-brand-peach/50 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-peach/20 to-brand-lavender/20 flex items-center justify-center mb-6">
                    <span className="font-heading text-2xl gradient-text">{index + 1}</span>
                  </div>
                  <h3 className="font-heading text-2xl text-text-dark mb-3">{phase.name}</h3>
                  <p className="text-text-dark/60">{phase.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-brand-peach/40" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} transition={{ delay: 0.6 }} className="text-center mt-12">
            <Link to="/how-it-works">
              <Button
                variant="outline"
                className="h-12 px-8 rounded-full border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all"
                data-testid="learn-more-btn"
              >
                Learn How It Works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-24 bg-gradient-to-br from-deep-violet to-[#2a1b3d]" data-testid="cost-comparison">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-4">
              The True Cost of Coordinated Support
            </h2>
            <p className="text-text-light/60 text-lg max-w-2xl mx-auto">
              Most people can't afford what it actually takes to navigate major transitions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Traditional Support */}
            <motion.div
              {...stagger}
              transition={{ delay: 0.2 }}
              className="p-8 rounded-2xl glass-dark"
              data-testid="traditional-cost-card"
            >
              <h3 className="font-heading text-xl text-text-light/80 mb-6">Traditional Support</h3>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-text-light/70">Emotional Readiness Support</span>
                  <span className="text-text-light font-semibold">$150-300/session</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-text-light/70">Career Coaching</span>
                  <span className="text-text-light font-semibold">$200-500/session</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-text-light/70">Resource Navigation</span>
                  <span className="text-text-light font-semibold">Dozens of hours</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-text-light/70">Case Management</span>
                  <span className="text-text-light font-semibold">$100-200/hour</span>
                </div>
              </div>
              <div className="pt-4 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <span className="text-text-light/80 font-medium">Monthly Total</span>
                  <span className="font-heading text-3xl text-brand-peach">
                    $<CounterAnimation end={3000} duration={2} className="text-brand-peach" />+
                  </span>
                </div>
                <p className="text-text-light/50 text-sm mt-2">Up to $10,000/month for comprehensive support</p>
              </div>
            </motion.div>

            {/* Become */}
            <motion.div
              {...stagger}
              transition={{ delay: 0.4 }}
              className="p-8 rounded-2xl bg-gradient-to-br from-brand-peach/20 to-brand-lavender/20 border border-brand-peach/30"
              data-testid="become-cost-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <BecomeLogo size="small" showText={false} />
                <h3 className="font-heading text-xl text-text-light">Become</h3>
              </div>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 py-3">
                  <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  <span className="text-text-light/90">Emotional Readiness Support</span>
                </div>
                <div className="flex items-center gap-3 py-3">
                  <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  <span className="text-text-light/90">Career Discovery & Planning</span>
                </div>
                <div className="flex items-center gap-3 py-3">
                  <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  <span className="text-text-light/90">Resource Coordination</span>
                </div>
                <div className="flex items-center gap-3 py-3">
                  <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  <span className="text-text-light/90">Change Management</span>
                </div>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-brand-lavender font-medium mb-2">Pricing coming soon.</p>
                <p className="text-text-light/70 text-sm leading-relaxed">
                  We're taking the time to get it right, because affordability isn't a feature for us. It's the whole point.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Placeholder */}
      <section className="py-24 bg-warm-bone" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-4">
              Transformation Stories
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <p className="text-text-dark/70 text-xl leading-relaxed mb-10">
              Our first transformation stories are being written right now. Be part of them.
            </p>
            <Link to="/contact">
              <Button
                className="h-12 px-8 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
                data-testid="stories-waitlist-btn"
              >
                Join Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 section-gradient" data-testid="differentiators-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-4">
              What Makes Us Different
            </h2>
            <p className="text-text-dark/60 text-lg max-w-2xl mx-auto">
              Built differently because transformation deserves better.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                {...stagger}
                transition={{ delay: index * 0.15 }}
                className="p-8 rounded-2xl bg-white shadow-soft border border-gray-100 card-hover"
                data-testid={`differentiator-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-peach to-brand-lavender flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-deep-violet" />
                </div>
                <h3 className="font-heading text-xl text-text-dark mb-3">{item.title}</h3>
                <p className="text-text-dark/60 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transition to How It Works */}
      <section className="py-20 bg-gradient-to-b from-warm-bone to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-primary font-medium tracking-widest uppercase text-sm mb-4">
              Curious How It Works?
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Discover the Journey
            </h2>
            <p className="text-text-dark/60 text-lg mb-8 max-w-2xl mx-auto">
              Learn how Become guides you through three transformative phases: Reflect, Discover, and Become.
            </p>
            <Link to="/how-it-works">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="learn-how-it-works-btn"
              >
                Learn How It Works
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 hero-dark" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-text-light mb-6">
              Ready to <span className="gradient-text">Become</span>?
            </h2>
            <p className="text-text-light/70 text-lg mb-10 max-w-2xl mx-auto">
              Be among the first to transform your life with coordinated, affordable support.
            </p>
            <WaitlistForm variant="dark" />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
